import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Image,
  Text,
  VStack,
  Button,
  HStack,
  Icon,
  Flex,
  useColorMode,
  IconButton,
  useBreakpointValue,
  Tooltip,
} from "@chakra-ui/react";
import { FaBed } from "react-icons/fa";
import {
  ArrowBackIcon,
  ArrowForwardIcon,
  StarIcon,
  DeleteIcon,
} from "@chakra-ui/icons";
import { FaRegHeart, FaHeart } from "react-icons/fa";
import axiosInstance from "../../helpers/axiosInstance";
import { useCurrency } from "../CurrencyExchange/CurrencyContext";
import Translate from "../../components/LanguageTranslation/Translate";
import ExternalLinkButton from "../ExternalLinkButton";
const HotelsCard = ({
  hotels = [],
  location,
  onActivityClick,
  category = "accommodation",
  itinerary = false,
  conversationId,
  cityName,
  onAccommodationsChange,
  fromItineray,
  fromChatItineray,
}) => {
  // console.log("hotels", hotels);
  const { colorMode } = useColorMode();
  const [current, setCurrent] = useState(0);
  const [likedActivities, setLikedActivities] = useState({});
  const { convertCurrency, currency } = useCurrency(); // Get the conversion function and selected currency from context

  // Track current image index for each hotel
  const [imageIndices, setImageIndices] = useState(hotels.map(() => 0));

  const numVisibleCards = useBreakpointValue({ base: 1, md: 2, lg: 2 });
  const numTotalCards = Array.isArray(hotels) ? hotels.length : 0;

  const containerRef = useRef(null);
  const touchStartXRef = useRef(null);
  const [showCallout, setShowCallout] = useState(true);
  const [isDeletingAll, setIsDeletingAll] = useState(false);
  const [isDeleting, setIsDeleting] = useState({});
  useEffect(() => {
    const timer = setTimeout(() => setShowCallout(false), 9000);
    return () => clearTimeout(timer);
  }, []); // run once on mount
  const next = () => {
    setCurrent((prev) => (prev + numVisibleCards) % numTotalCards);
  };
  const prev = () => {
    setCurrent(
      (prev) => (prev - numVisibleCards + numTotalCards) % numTotalCards
    );
  };
  /* ───────── image helpers ───────── */
  const changeImg = (absIndex, dir) =>
    setImageIndices((prev) => {
      const copy = [...prev];
      const imgs = hotels[absIndex]?.images ?? [];
      if (imgs.length)
        copy[absIndex] = (copy[absIndex] + dir + imgs.length) % imgs.length;
      return copy;
    });
  const handleTouchStart = (e) => {
    touchStartXRef.current = e.touches[0].clientX;
  };
  const handleTouchEnd = (e) => {
    const touchEndX = e.changedTouches[0].clientX;
    const diff = touchStartXRef.current - touchEndX;
    if (diff > 50) next();
    else if (diff < -50) prev();
  };
  const hasPrev = current > 0;
  const hasNext = current + numVisibleCards < numTotalCards;
  const handleNextImage = (hotelIndex) => {
    setImageIndices((prevIndices) => {
      const newIndices = [...prevIndices];
      const currentHotel = hotels[hotelIndex];
      newIndices[hotelIndex] =
        (prevIndices[hotelIndex] + 1) % currentHotel.images.length;
      return newIndices;
    });
  };
  const handlePrevImage = (hotelIndex) => {
    setImageIndices((prevIndices) => {
      const newIndices = [...prevIndices];
      const currentHotel = hotels[hotelIndex];
      newIndices[hotelIndex] =
        (prevIndices[hotelIndex] - 1 + currentHotel.images.length) %
        currentHotel.images.length;
      return newIndices;
    });
  };

  const isActivityLiked = (idx, hotel) =>
    likedActivities[idx] ?? hotel.added_to_bucket_list ?? false;

  const sendLikedActivity = async (hotel, added) => {
    const payload = {
      conversation_data: hotel,
      // category: category,
      conversation_type: "accommodation_conversation",
      added_to_bucket_list: added,
    };
    try {
      await axiosInstance.post(
        `/chat/conversation/update-conversation-heart-icon/${conversationId}`,
        payload
      );
      // Call the second endpoint only when hotel is liked
      if (added) {
        const bucketPayload = {
          data: hotel,
          category: category,
          conversation_type: "accommodation_conversation",
        };
        await axiosInstance.post(
          "/bucket-list/save-third-party-service",
          bucketPayload
        );
      }
      // console.log(`Activity ${isLiked ? "liked" : "unliked"}:`, hotel);
    } catch (error) {
      console.error("Error saving liked activity:", error);
    }
  };

  // const toggleLike = (index, hotel) => {
  //   const updatedLikes = { ...likedActivities };
  //   const isLiked = !updatedLikes[index];

  //   if (isLiked) {
  //     updatedLikes[index] = hotel; // Add activity if liked
  //   } else {
  //     delete updatedLikes[index]; // Remove activity if unliked
  //   }

  //   setLikedActivities(updatedLikes);

  //   // Send only the current activity to backend
  //   sendLikedActivity(hotel, isLiked);
  // };

  const toggleLike = (index, hotel) => {
    const currentlyLiked = isActivityLiked(index, hotel);
    const newLiked = !currentlyLiked; // white → red  OR  red → white

    setLikedActivities((prev) => ({ ...prev, [index]: newLiked }));
    sendLikedActivity(hotel, newLiked); // <-- pass TRUE or FALSE
  };

  // Delete a specific accommodation
  const handleDeleteAccommodation = async (accommodationIndex) => {
    if (!conversationId || !cityName) return;
    setIsDeleting((prev) => ({ ...prev, [accommodationIndex]: true }));
    try {
      await axiosInstance.delete(
        `/chat/conversation/delete-accommodation-info/${conversationId}`,
        {
          data: {
            city_name: cityName,
            accommodation_index: accommodationIndex,
          },
        }
      );
      if (onAccommodationsChange)
        onAccommodationsChange(accommodationIndex, "single");
    } catch (error) {
      console.error("Error deleting accommodation:", error);
    } finally {
      setIsDeleting((prev) => ({ ...prev, [accommodationIndex]: false }));
    }
  };

  return (
    <Flex
      position="relative"
      alignItems="center"
      justifyContent="center"
      padding={0}
      mt={3}
      width="100%"
    >
      {hasPrev && (
        <IconButton
          icon={<ArrowBackIcon />}
          isRound={true}
          size="md"
          onClick={prev}
          position="absolute"
          left={{ base: "1px", md: "10px" }}
          backgroundColor="white"
          boxShadow="base"
          zIndex="overlay"
        />
      )}
      <HStack
        ref={containerRef}
        overflow="hidden"
        width="full"
        h="auto"
        spacing={3}
        justifyContent={{ base: "center", md: "space-between" }}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        {hotels
          .slice(current, current + numVisibleCards)
          .map((hotel, index) => {
            const actualIndex = current + index;
            {/* const isLiked = likedActivities[actualIndex] !== undefined; */}
            const liked = isActivityLiked(actualIndex, hotel);
            // Add console log to debug the values here
            const convertedPrice = convertCurrency(
              hotel.price.perNight,
              hotel.price.currency
            );
            const idx = current + index;
            const imgIdx = imageIndices[idx] ?? 0; // guarded
            const imgSrc =
              `https:${hotel.images?.[imgIdx]?.large ?? ""}` ||
              "https://via.placeholder.com/300x200.png?text=No+Image";
            return (
              <Box
                key={index}
                bg="gray.100"
                padding={2}
                borderRadius="xl"
                width={{ base: "80%", md: "50%", lg: "50%" }}
                display="flex"
                flexDirection="column"
                alignItems="center"
                onClick={() =>
                  onActivityClick &&
                  onActivityClick(
                    hotel.geoLocation?.lat,
                    hotel.geoLocation?.lon
                  )
                }
                _hover={{ cursor: "pointer" }}
              >
                <Flex
                  alignContent="top"
                  justifyContent="space-between"
                  width="100%"
                >
                  <HStack spacing={2} mb={1}>
                    <Icon as={FaBed} color="#286328" boxSize={4} />
                    <Text
                      fontWeight="bold"
                      fontSize="sm"
                      mb={0}
                      noOfLines={1}
                      sx={{
                        display: "-webkit-box",
                        WebkitLineClamp: "1",
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {hotel.type.replace(/_/g, " ")}
                    </Text>
                  </HStack>
                </Flex>
                <Box
                  borderTopRadius="md"
                  boxShadow="md"
                  width="100%"
                  position="relative"
                >
                  <IconButton
                    icon={<ArrowBackIcon />}
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      changeImg(idx, -1); // 🔸 ABS index
                    }}
                    position="absolute"
                    left="1px"
                    top="50%"
                    transform="translateY(-50%)"
                    zIndex="1"
                    color="white"
                    bg="rgba(0, 0, 0, 0.6)"
                    _hover="none"
                  />
                  <Image
                    src={imgSrc}
                    fallbackSrc="https://via.placeholder.com/300x200.png?text=No+Image"
                    alt={hotel.title || "Hotel"}
                    borderTopRadius="xl"
                    objectFit="cover"
                    width="100%"
                    height="200px"
                  />
                  <Box
                    position="absolute"
                    top="3px"
                    right="3px"
                    display="flex"
                    flexDirection="row"
                    alignItems="center"
                    gap="0px"
                  >
                    {fromChatItineray && (
                      <Tooltip label="Delete Accommodation">
                        <IconButton
                          aria-label="Delete accommodation"
                          icon={<DeleteIcon />}
                          size="md"
                          colorScheme="red"
                          isLoading={isDeleting[actualIndex]}
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            handleDeleteAccommodation(actualIndex);
                          }}
                          borderRadius="full"
                          bg="none"
                          _hover={{ bg: "none" }}
                        />
                      </Tooltip>
                    )}
                    {!fromItineray && !fromChatItineray && (
                      <Tooltip
                        label={
                          liked
                            ? "Remove from bucket list"
                            : "Add to bucket list"
                        }
                      >
                        <IconButton
                          aria-label={
                            liked
                              ? "Remove from bucket list"
                              : "Add to bucket list"
                          }
                          icon={liked ? <FaHeart /> : <FaRegHeart />}
                          size="md"
                          color={liked ? "red.500" : "white"}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleLike(actualIndex, hotel);
                          }}
                          borderRadius="full"
                          bg="none"
                          _hover={{ bg: "none" }}
                          marginLeft="-12px"
                        />
                      </Tooltip>
                    )}
                  </Box>
                  <IconButton
                    icon={<ArrowForwardIcon />}
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      changeImg(idx, +1); // 🔸 ABS index
                    }}
                    position="absolute"
                    right="1px"
                    top="50%"
                    transform="translateY(-50%)"
                    zIndex="1"
                    bg="rgba(0, 0, 0, 0.6)"
                    color="white"
                    _hover="none"
                  />
                </Box>
                <VStack
                  alignItems="flex-start"
                  width="100%"
                  p={3}
                  spacing={2}
                  textAlign="left"
                  bg="white"
                  borderBottomRadius="xl"
                >
                  <Text
                    fontWeight="bold"
                    fontSize="md"
                    noOfLines={1}
                    sx={{
                      display: "-webkit-box",
                      WebkitLineClamp: "1",
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                    mb={0}
                  >
                    <Translate text={hotel.title} />
                  </Text>
                  {hotel.rating?.value ? (
                    <HStack spacing={1}>
                      <StarIcon boxSize={3} color="#3A4135" mb={0} />
                      <Text
                        color="#1A202C"
                        fontWeight="bold"
                        fontSize="sm"
                        mb={0}
                      >
                        {(hotel.rating.value / 10).toFixed(1)}
                      </Text>
                      <Text fontSize="xs" color="gray.500" mb={0}>
                        ({hotel.rating.value} reviews)
                      </Text>
                    </HStack>
                  ) : (
                    <Text color="gray.500" fontSize="sm" mb={0}>
                      <Translate text="No reviews available" />
                    </Text>
                  )}
                  <HStack spacing={1} alignItems="flex-start">
                    <Text
                      fontWeight="bold"
                      fontSize="sm"
                      color="gray.800"
                      mb={0}
                    >
                      {currency} {convertedPrice.toLocaleString()}{" "}
                    </Text>
                    <Text fontSize="sm" color="gray.800" mb={0}>
                      <Translate text="night" />
                    </Text>
                  </HStack>
                  <ExternalLinkButton
                    as="a"
                    href={hotel.link}
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                    mt={2}
                    size="sm"
                    bg="#286328"
                    _hover="none"
                    _active="none"
                    textColor="white"
                    width="full"
                  >
                    <Translate text="Open" />
                  </ExternalLinkButton>
                </VStack>
              </Box>
            );
          })}
      </HStack>
      {hasNext && (
        <IconButton
          icon={<ArrowForwardIcon />}
          isRound={true}
          size="md"
          onClick={next}
          position="absolute"
          right={{ base: "1px", md: "10px" }}
          backgroundColor="white"
          boxShadow="base"
          zIndex="overlay"
        />
      )}
    </Flex>
  );
};
export default HotelsCard;
